import toast, { Toaster } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import useFormWithSchema from "../../../hooks/useFormWithSchema";
import {
  useGetAllBuildingsById,
  useGetAllDevices,
  useGetAllFRDevicesByLocDD,
  useGetAllLocationsDropdown,
  useGetAllSystemDetailCodesDropdown,
  useGetFRDeviceById,
} from "../../../hooks/common/query";
import { Controller } from "react-hook-form";
import TextInput from "../../../components/inputs/TextInput";
import { Button } from "../../../components/button";
import { mapToOptions } from "../../../lib/functions";

import BackButtonWrapper from "../../../components/button/BackButton/wrapper";
import { FRDevicesSchema } from "../../../schemas/frDevicesSchema";
import ComboboxInput from "../../../components/inputs/ComboboxInput";
import {
  DEVICE_TYPES,
  PAGE_SIZE,
  SYSTEM_DETAIL_CODES,
} from "../../../constants/common";
import { Device } from "../../../hooks/common/interface";
import {
  useAddUpdateDevice,
  useIsDeviceExists,
  useIsIPAddressExists,
} from "../../../hooks/common/mutation";
import DevicesConfigTable from "../../../components/table/DevicesConfigTable";
import { useGetMusterPointsDropdownByLocation } from "../../../hooks/emergency-evacuation/query";

interface FormData {
  deviceNameEn: string;
  deviceNameAr: string;
  deviceType: string;
  device?: string;
  ipAddress: string;
  description?: string;
  location: string;
  building: string;
  musterPoint?: string;
}

const initialData = {
  deviceNameEn: "",
  deviceNameAr: "",
  deviceType: "",
  device: "",
  ipAddress: "",
  description: "",
  location: "",
  building: "",
  musterPoint: "",
};

const FRDevicesConfig = () => {
  const { t } = useTranslation();

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    clearErrors,
    setValue,
    watch,
  } = useFormWithSchema(FRDevicesSchema);

  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState(1);
  const [count, setCount] = useState(0);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);
  const [search, setSearch] = useState("");

  const [allDevices, setAllDevices] = useState<Device[]>([]);

  const { data: deviceTypes } = useGetAllSystemDetailCodesDropdown({
    encryptedId: SYSTEM_DETAIL_CODES.DEVICE_TYPES,
  });

  const allDeviceTypes = useMemo(
    () => mapToOptions("s_Desc_E", "s_Desc_A", "n_DetailedCode", deviceTypes),
    [deviceTypes],
  );

  const { mutateAsync: IsDeviceExists } = useIsDeviceExists();
  const { mutateAsync: IsIPAddressExists } = useIsIPAddressExists();

  const { data: allLocations } = useGetAllLocationsDropdown();

  const { data: devices } = useGetAllDevices({
    pageNumber: page,
    pageSize: pageSize,
    search: search,
  });

  const [selectedDeviceId, setSelectedDeviceId] = useState(0);

  const { mutateAsync: AddUpdateDevice, isPending: isAddUpdateDeviceLoading } =
    useAddUpdateDevice();

  useEffect(() => {
    if (devices) {
      setAllDevices(devices.data || []);
      setPages(devices.pagination?.pages || 1);
      setCount(devices.pagination?.count || 0);
    }
  }, [setPages, devices]);

  const { data: selectedDevice } = useGetFRDeviceById({
    encryptedId: selectedDeviceId ? selectedDeviceId.toString() : "",
  });

  const selectedLocation = watch("location");
  const building = watch("building");
  const selectedDeviceType = watch("deviceType") as unknown as DEVICE_TYPES;

  const { data: allFRDevicesByLocDD } = useGetAllFRDevicesByLocDD(
    {
      encryptedLocationID: selectedLocation ? selectedLocation?.toString() : "",
      encryptedFRDeviceTypeID:
        selectedDeviceType === DEVICE_TYPES.KIOSK
          ? DEVICE_TYPES.FR?.toString()
          : "",
      n_BuildingID: building ? Number(building) : 0,
    },
    selectedDeviceType === DEVICE_TYPES.KIOSK,
  );

  const { data: allMusterPoints } = useGetMusterPointsDropdownByLocation({
    n_LocationID: selectedLocation ? Number(selectedLocation) : 0,
    n_BuildingID: building ? Number(building) : 0,
  });

  const musterPoints = useMemo(
    () =>
      mapToOptions(
        "s_MusterPointName_En",
        "s_MusterPointName_Ar",
        "n_MusterPointID",
        allMusterPoints,
      ),
    [allMusterPoints],
  );

  useEffect(() => {
    if (selectedDevice) {
      setValue("deviceNameEn", selectedDevice.s_DeviceName_En);
      setValue("deviceNameAr", selectedDevice.s_DeviceName_Ar);
      setValue("deviceType", selectedDevice.n_FRDeviceType);
      setValue("ipAddress", selectedDevice.s_IPAddress);
      setValue("description", selectedDevice.s_Description || "");
      setValue("location", selectedDevice.n_LocationID);
      setValue("building", selectedDevice.n_BuildingID);
      setValue("device", selectedDevice.n_KioskFRDeviceID);
      setValue("musterPoint", selectedDevice.n_MusterPointID);
    }
  }, [selectedDevice, setValue]);

  const frDevicesDD = useMemo(
    () =>
      mapToOptions(
        "s_DeviceName_En",
        "s_DeviceName_Ar",
        "n_DeviceID",
        allFRDevicesByLocDD,
      ),
    [allFRDevicesByLocDD],
  );

  const locations = useMemo(
    () =>
      mapToOptions(
        "s_LocationName_En",
        "s_LocationName_Ar",
        "n_LocationID",
        allLocations,
      ),
    [allLocations],
  );

  const selectedLocationId = watch("location");

  const { data: allBuildings } = useGetAllBuildingsById({
    encryptedId: selectedLocationId ? selectedLocationId.toString() : "",
  });

  const buildings = useMemo(
    () =>
      allBuildings
        ? mapToOptions(
            "s_BuildingName_En",
            "s_BuildingName_Ar",
            "n_BuildingID",
            allBuildings,
          )
        : [],
    [allBuildings],
  );

  const onSubmit = useCallback(
    async (data: FormData) => {
      try {
        const isExistBody = {
          encryptedId: selectedDeviceId ? selectedDeviceId?.toString() : "",
          s_DeviceName_En: data.deviceNameEn,
          s_DeviceName_Ar: data.deviceNameAr,
          n_LocationID: Number(data.location),
          n_BuildingID: Number(data.building),
        };
        const { isExists } = await IsDeviceExists(isExistBody);

        if (isExists) {
          toast.error(t("deviceExists"));
          return;
        }
      } catch (error) {
        toast.error(t("somethingWentWrong"));
        console.error("Error checking if device exists", error);
        return;
      }

      try {
        const isIpAddressBody = {
          encryptedId: selectedDeviceId ? selectedDeviceId?.toString() : "",
          s_IPAddress: data.ipAddress,
        };
        const { isExists } = await IsIPAddressExists(isIpAddressBody);

        if (isExists) {
          toast.error(t("ipAddressExists"));
          return;
        }
      } catch (error) {
        toast.error(t("somethingWentWrong"));
        console.error("Error checking if IP address exists", error);
        return;
      }

      const isKiosk = Number(data.deviceType) === DEVICE_TYPES.KIOSK;
      const isFR = Number(data.deviceType) === DEVICE_TYPES.FR;

      try {
        const body = {
          n_DeviceID: 0,
          s_DeviceName_En: data.deviceNameEn,
          s_DeviceName_Ar: data.deviceNameAr,
          s_Description: data.description,
          n_FRDeviceType: Number(data.deviceType),
          s_IPAddress: data.ipAddress,
          n_LocationID: Number(data.location),
          n_BuildingID: Number(data.building),
          ...(isKiosk && { n_KioskFRDeviceID: Number(data.device) }),
          ...(isFR && { N_MusterPointID: Number(data.musterPoint) }),
        };

        if (selectedDeviceId === 0) {
          await AddUpdateDevice(body);
        } else {
          const updateBody = {
            ...body,
            n_DeviceID: selectedDeviceId,
          };
          await AddUpdateDevice(updateBody);
        }

        setSelectedDeviceId(0);

        if (selectedDeviceId === 0) {
          toast.success(t("deviceAdded"));
        } else {
          toast.success(t("deviceUpdated"));
        }
        reset(initialData);
      } catch (error) {
        toast.error(t("somethingWentWrong"));
        console.error("Error adding/updating devices", error);
      }
    },
    [
      AddUpdateDevice,
      IsDeviceExists,
      IsIPAddressExists,
      reset,
      selectedDeviceId,
      t,
    ],
  );

  const onClear = useCallback(() => {
    setSelectedDeviceId(0);
    reset(initialData);
  }, [reset]);

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <div className="flex flex-col gap-4 md:gap-8">
        <BackButtonWrapper>
          <h1 className="text-xl md:text-2xl dark:text-white">
            {t("devicesConfig")}
          </h1>
        </BackButtonWrapper>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4 md:gap-8"
        >
          <div className="bg-white p-4 rounded-xl shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)] flex flex-col gap-4">
            <div className="grid sm:grid-cols-3 gap-3 lg:gap-6 ">
              <Controller
                name="deviceNameEn"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <div className="flex flex-col">
                    <TextInput
                      label={t("deviceNameEn")}
                      value={value || ""}
                      onChange={onChange}
                      error={errors?.[name]?.message}
                      required
                    />
                  </div>
                )}
              />

              <Controller
                name="deviceNameAr"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <div className="flex flex-col">
                    <TextInput
                      label={t("deviceNameAr")}
                      value={value || ""}
                      onChange={onChange}
                      error={errors?.[name]?.message}
                      required
                    />
                  </div>
                )}
              />

              <Controller
                name="ipAddress"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <div className="flex flex-col">
                    <TextInput
                      label={t("ipAddress")}
                      value={value || ""}
                      onChange={onChange}
                      error={errors?.[name]?.message}
                      required
                    />
                  </div>
                )}
              />

              <Controller
                name="location"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={locations}
                    value={
                      locations?.find((item) => item.value === value)
                        ?.labelEn || ""
                    }
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    label={t("location")}
                    required
                  />
                )}
              />

              <Controller
                name="building"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={buildings}
                    value={
                      buildings?.find((item) => item.value === Number(value))
                        ?.labelEn || ""
                    }
                    required
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    label={t("building")}
                  />
                )}
              />

              <Controller
                name="deviceType"
                control={control}
                render={({ field: { onChange, name, value } }) => {
                  return (
                    <ComboboxInput
                      options={allDeviceTypes}
                      value={
                        allDeviceTypes?.find((item) => item.value === value)
                          ?.labelEn || ""
                      }
                      onChange={(value) => {
                        onChange(value);
                        clearErrors(name);
                      }}
                      error={errors?.[name]?.message}
                      label={t("deviceType")}
                      required
                    />
                  );
                }}
              />

              {selectedDeviceType === DEVICE_TYPES.KIOSK && (
                <Controller
                  name="device"
                  control={control}
                  render={({ field: { onChange, name, value } }) => {
                    return (
                      <ComboboxInput
                        options={frDevicesDD}
                        value={
                          frDevicesDD?.find((item) => item.value === value)
                            ?.labelEn || ""
                        }
                        onChange={(value) => {
                          onChange(value);
                          clearErrors(name);
                        }}
                        error={errors?.[name]?.message}
                        label={t("frDevice")}
                      />
                    );
                  }}
                />
              )}

              <Controller
                name="description"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <div className="flex flex-col">
                    <TextInput
                      label={t("description")}
                      value={value || ""}
                      onChange={onChange}
                      error={errors?.[name]?.message}
                    />
                  </div>
                )}
              />

              {selectedDeviceType === DEVICE_TYPES.FR && (
                <Controller
                  name="musterPoint"
                  control={control}
                  render={({ field: { onChange, name, value } }) => {
                    return (
                      <ComboboxInput
                        options={musterPoints}
                        value={
                          musterPoints?.find((item) => item.value === value)
                            ?.labelEn || ""
                        }
                        onChange={(value) => {
                          onChange(value);
                          clearErrors(name);
                        }}
                        error={errors?.[name]?.message}
                        label={t("musterPoint")}
                      />
                    );
                  }}
                />
              )}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <Button isLoading={isAddUpdateDeviceLoading}>{t("save")}</Button>
              <Button
                type="button"
                onClick={onClear}
                className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
              >
                {t("clear")}
              </Button>
            </div>
          </div>
          <DevicesConfigTable
            devices={allDevices}
            setSelectedDeviceId={setSelectedDeviceId}
            page={page}
            pages={pages}
            setPage={setPage}
            search={search}
            setSearch={setSearch}
            count={count}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        </form>
      </div>
    </>
  );
};

export default FRDevicesConfig;
